import React, { useEffect, useRef, useState } from 'react';
import { getHeroTemplate, getHeroStackStyle, heroRegistry, HeroConfig } from '../branding/hero';
import { Header, HeaderConfig } from '../branding/header';
import { FooterConfig, FooterOfficeInfo, getFooterTemplate } from '../branding/footer';
import { PreviewContenido, usePreviewMessaging } from './usePreviewMessaging';
import DemoBanners from './DemoBanners';
import ContenidoBlock from '../contenido/ContenidoBlock';
import { emptyDatos, isContentTemplateId } from '../contenido/templates';
import FloatingButtonGroup from 'legacyfrontend/components/ui/FloatingButtonGroup';
import './PreviewShell.css';

declare global {
  interface Window {
    __PREVIEW_TEMPLATE__?: string;
  }
}

function usePreviewNavigationGuard(containerRef: React.RefObject<HTMLElement>): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    function blockAnchorNavigation(event: MouseEvent): void {
      const target = event.target;
      if (target instanceof Element && target.closest('a[href]')) {
        event.preventDefault();
      }
    }

    function blockFormSubmission(event: SubmitEvent): void {
      event.preventDefault();
    }

    container.addEventListener('click', blockAnchorNavigation, true);
    container.addEventListener('submit', blockFormSubmission, true);

    return () => {
      container.removeEventListener('click', blockAnchorNavigation, true);
      container.removeEventListener('submit', blockFormSubmission, true);
    };
  }, [containerRef]);
}

const PreviewRoute: React.FC = () => {
  const [templateCode, setTemplateCode] = useState<string>(
    () => window.__PREVIEW_TEMPLATE__ || heroRegistry[0].code,
  );
  const [heroConfig, setHeroConfig] = useState<Partial<HeroConfig>>({});
  const [headerConfig, setHeaderConfig] = useState<Partial<HeaderConfig>>({});
  const [footerConfig, setFooterConfig] = useState<Partial<FooterConfig>>({});
  const [footerTemplateCode, setFooterTemplateCode] = useState<string>('standard');
  const [officeInfo, setOfficeInfo] = useState<FooterOfficeInfo | undefined>(undefined);
  const [contenido, setContenido] = useState<PreviewContenido | undefined>(undefined);
  // The menu drawer portals into this node (instead of document.body) so it
  // stays confined to the header+hero block, with its own internal scroll,
  // instead of covering the full page down past the footer.
  const [stackNode, setStackNode] = useState<HTMLDivElement | null>(null);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  usePreviewNavigationGuard(containerRef);

  const { notifySearcherChange, notifyModalOpen, notifyModalClose } = usePreviewMessaging(
    containerRef,
    (message) => {
      setTemplateCode(message.templateCode);
      setHeroConfig(message.heroConfig);
      setHeaderConfig(message.headerConfig);
      setFooterConfig(message.footerConfig);
      setFooterTemplateCode(
        message.footerTemplateCode || message.footerConfig?.templateCode || 'standard',
      );
      setOfficeInfo(message.officeInfo);
      setContenido(message.contenido);
    },
  );

  // Close the Default mobile lupa panel only when the header template changes —
  // not on every config sync (searcher tab clicks re-post config from the parent).
  useEffect(() => {
    setMobileSearchOpen(false);
  }, [templateCode]);

  // Clicking a searcher inside the iframe behaves like the mockup: it becomes the
  // active one here and the parent form is notified so both stay in sync.
  function handleSearcherSelect(searcherId: string): void {
    setHeaderConfig((current) => ({ ...current, activeSearcherId: searcherId }));
    setHeroConfig((current) => ({ ...current, activeSearcherTabId: searcherId }));
    notifySearcherChange(searcherId);
  }

  const { Component, headerVariant: variant } = getHeroTemplate(templateCode);
  const { Component: FooterComponent } = getFooterTemplate(footerTemplateCode);
  const isFullBleed = variant === 'hero_a' && headerConfig.fullBleed === true;
  const stackStyle = getHeroStackStyle(heroConfig, isFullBleed, {
    reserveButtonColor: headerConfig.brandColor2 || '#152956',
    iconColor: headerConfig.iconColor || '#225e8f',
  });
  const hasMobileBg = Boolean((heroConfig.mobileBackgroundImageUrl || '').trim());
  const mobileSearchInline = variant === 'hero_a' && hasMobileBg;
  const stackClassName = [
    'preview-hero-stack',
    `preview-hero-stack--${templateCode}`,
    isFullBleed && 'preview-hero-stack--full-bleed',
    (variant === 'hero_a' || variant === 'reserve' || variant === 'ecommerce_slider') &&
      mobileSearchOpen &&
      'mobile-search-open',
    variant === 'hero_a' && mobileSearchInline && 'mobile-search-inline',
  ]
    .filter(Boolean)
    .join(' ');

  const contactStrip =
    variant === 'reserve'
      ? {
          contactColor: headerConfig.contactColor,
          phone: headerConfig.phone,
          showPhone: headerConfig.showPhone,
          email: headerConfig.email,
          showEmail: headerConfig.showEmail,
          socialLinks: headerConfig.socialLinks,
        }
      : undefined;

  return (
    <div className='preview-root' ref={containerRef}>
      <div className={stackClassName} ref={setStackNode} style={stackStyle}>
        <Header
          config={headerConfig}
          heroSearchConfig={heroConfig}
          menuPortalContainer={stackNode}
          mobileSearchOpen={mobileSearchOpen}
          onMobileSearchToggle={() => setMobileSearchOpen((open) => !open)}
          onSearcherSelect={handleSearcherSelect}
          paisId={officeInfo?.paisId}
          variant={variant}
        />
        <Component
          config={heroConfig}
          contactStrip={contactStrip}
          mobileSearchOpen={mobileSearchOpen}
          onSearcherSelect={handleSearcherSelect}
          paisId={officeInfo?.paisId}
        />
      </div>
      <DemoBanners
        content={
          contenido && isContentTemplateId(contenido.templateId) ? (
            <ContenidoBlock
              acento={contenido.acento || '#f1c050'}
              animaciones={contenido.animaciones !== false}
              datos={contenido.datos || emptyDatos(contenido.templateId)}
              mode='preview'
              templateId={contenido.templateId}
            />
          ) : undefined
        }
      />
      <FooterComponent
        config={{
          ...footerConfig,
          agencyLogoUrl: headerConfig.logoImageUrl,
          templateCode: footerTemplateCode,
        }}
        contact={{
          phone: headerConfig.phone,
          showPhone: headerConfig.showPhone,
          email: headerConfig.email,
          showEmail: headerConfig.showEmail,
          socialLinks: headerConfig.socialLinks,
        }}
        officeInfo={officeInfo}
        onModalClose={notifyModalClose}
        onModalOpen={notifyModalOpen}
      />
      {headerConfig.showWhatsapp && headerConfig.whatsapp ? (
        <FloatingButtonGroup phoneNumber={headerConfig.whatsapp} />
      ) : null}
    </div>
  );
};

export default PreviewRoute;
